import { useState } from "react";

/**
 * Custom hook to store state in local storage and rehydrate it on initialization.
 * @param {string} key The key under which to store the state in local storage.
 * @param {*} initialValue The initial state value or a function to compute it.
 * @param {Function} [deserialize] Optional function to deserialize the stored value.
 * @returns A stateful value, and a function to update it.
 */
function useLocalStorage(key, initialValue, deserialize) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);

      if (item) {
        try {
          const parsedItem = JSON.parse(item); // Try parsing as JSON

          // Use the deserialize function if provided
          return deserialize ? deserialize(parsedItem) : parsedItem;
        } catch (error) {
          return deserialize ? deserialize(item) : item; // Return as is if it's not valid JSON
        }
      } else {
        return initialValue;
      }
    } catch (error) {
      console.log(error);
      console.log(key);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      // Determine whether to store it as JSON or a plain string
      if (typeof valueToStore === "object") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore)); // Store objects/arrays as JSON
      } else {
        window.localStorage.setItem(key, valueToStore); // Store plain strings as is
      }
    } catch (error) {
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
